import React, {useState, useEffect, memo} from "react";
import PropTypes from "prop-types";
import {extendJavascript} from "../netGraph/extendNativeJavascript";
import * as d3 from "d3";
import ForceDirectedGraph from "../netGraph/ForceDirectedGraph";
import {mergeObjects} from "../netGraph/DataProcessing/mergeObjects";

export const BasicNetgraph = memo(
    ({
        elementId = "netgraph",
        data,
        config: customConfig = {},
        aspectRatio = 16 / 9,
        nodeSelectionCallback = () => {},
        nodeUnselectionCallback = () => {},
        selectedNodeName,
    }) => {
        const defaultConfig = require("../netGraph/configs/default-config.json");
        const element = <div id={elementId} className="h-100 w-100" />;
        const [graph, setGraph] = useState();
        const [selectedNode, setSelectedNode] = useState({});

        useEffect(() => {
            if (graph) {
                const node = getNodeByName(selectedNodeName);
                if (node) {
                    const originalNode = node["original"];
                    if (originalNode) {
                        setSelectedNode(originalNode);
                        graph.selectNode(node);
                    }
                } else {
                    setSelectedNode({});
                    graph.unselectNodes();
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedNodeName]);

        useEffect(() => {
            if (selectedNode) nodeSelectionCallback(selectedNode);
            else {
                nodeUnselectionCallback();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedNode]);

        useEffect(() => {
            d3.select("#" + elementId).append("svg");
            extendJavascript();
        }, []);

        useEffect(() => {
            const params = computeConfig();
            if (params !== undefined) {
                if (graph !== undefined) {
                    graph.clear();
                }
                createGraph(params);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [customConfig, aspectRatio]);

        useEffect(updateGraph, [graph, data]);

        function createGraph(params) {
            let newGraph = new ForceDirectedGraph(params);
            setGraph(newGraph);
        }

        function updateGraph() {
            if (graph && data && isDataFormatOkay(data)) {
                graph.plot(data);
            }
        }

        function getNodeByName(name) {
            if (name && data) {
                const nodes = data["nodes"];
                const node = nodes.find(d => d.original.name.toLowerCase() === name.toLowerCase());
                if (node) return node;
            }
            return null;
        }

        function computeConfig() {
            let config = mergeObjects(defaultConfig, customConfig, false);
            config["aspectRatio"] = aspectRatio;
            config["nodesOnclickFunc"] = d => setSelectedNode(d.original);
            config["backgroundOnclickFunc"] = nodeUnselectionCallback;
            config["elementId"] = elementId;
            return config;
        }

        return element;

        function isDataFormatOkay(data) {
            if ("nodes" in data && "links" in data) {
                if (Array.isArray(data["nodes"]) && Array.isArray(data["links"])) {
                    return true;
                }
            }
            return false;
        }
    },
    (prevProps, nextProps) =>
        prevProps.config === nextProps.config &&
        prevProps.data === nextProps.data &&
        prevProps.selectedNodeName === nextProps.selectedNodeName &&
        prevProps.aspectRatio === nextProps.aspectRatio
);

BasicNetgraph.propTypes = {
    data: PropTypes.object,
    config: PropTypes.object,
    aspectRatio: PropTypes.number,
    nodeSelectionCallback: PropTypes.func,
    nodeUnselectionCallback: PropTypes.func,
};
